import type { AxiosError } from 'axios';

import type { DependenciesResponse, MetricStat } from '@/schemas/commons';
import type { PauseState } from '@/services/Pause';
import type { GraphRoutingResponse } from '@/services/Playground';
import { findCreditExhaustion } from '@/utils/creditExhaustion';

import { DEFAULT_PAUSE_ERROR_KEY, PAUSE_ERROR_KEY } from './Dashboard.constants';
import { styles } from './Dashboard.styles';

export const sparkPoints = (values: number[]): string => {
	if (values.length === 0) return '';
	const max = Math.max(...values);
	const min = Math.min(...values);
	const range = max - min || 1;

	return values
		.map((value, index) => {
			const x = (index / (values.length - 1)) * 60;
			const y = 22 - ((value - min) / range) * 18;

			return `${x.toFixed(1)},${y.toFixed(1)}`;
		})
		.join(' ');
};

export const deltaClass = (stat: MetricStat): string => {
	if (stat.deltaDirection === 'up') return styles.deltaUp;
	if (stat.deltaDirection === 'down') return styles.deltaDown;
	return styles.deltaFlat;
};

export const pauseDotClass = (state?: PauseState): string => {
	if (!state) return styles.dotMuted;
	return state.paused ? styles.dotDanger : styles.dotSuccess;
};

export const hasCreditExhaustion = (data?: DependenciesResponse): boolean =>
	!!data && !!findCreditExhaustion(data.dependencies);

export const creditsDotClass = (data?: DependenciesResponse): string => {
	if (!data) return styles.dotMuted;
	return hasCreditExhaustion(data) ? styles.dotDanger : styles.dotSuccess;
};

export const alertsDotClass = (count: number | null): string => {
	if (count === null) return styles.dotMuted;
	return count > 0 ? styles.dotDanger : styles.dotSuccess;
};

/** Share of production traffic the default graph version carries right now. */
export const trafficSharePct = (routing: GraphRoutingResponse): number =>
	Math.round(routing.ramp[routing.default] ?? 100);

export const pauseErrorKey = (error: AxiosError | null): string | null => {
	if (!error) return null;
	return PAUSE_ERROR_KEY[error.response?.status ?? 0] ?? DEFAULT_PAUSE_ERROR_KEY;
};
